// Restaurant Table API service for backend integration
import { API_CONFIG, API_ENDPOINTS } from '../constants/config';
import type {
  RestaurantTable,
  RestaurantTableRequest,
  PaginatedResponse,
  ReserveTableDTO
} from '../types/reservations';
import { ErrorHandlers } from '../utils/errorHandler';

class RestaurantTableApiService {
  constructor() {
    // Constructor is empty since we're using relative URLs with proxy
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<T> {
    // In development, use relative URLs to leverage Vite proxy (same-origin cookies)
    // In production, use the full backend URL (requires backend CORS configuration)
    const isDev = import.meta.env.DEV;
    const baseUrl = isDev ? '' : (import.meta.env.VITE_API_URL || 'https://admin.grandpittu.lk');
    const url = `${baseUrl}${API_CONFIG.SLUG}${endpoint}`;

    // Headers for the request - no need for Authorization header, cookies are sent automatically
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

      const response = await fetch(url, {
        ...options,
        // Include credentials (cookies) for HTTP-only cookie auth
        credentials: 'include',
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.includes('application/json')) {
        // Handle non-JSON responses
        const text = await response.text();

        // If response is OK (2xx) and contains success message, treat as success
        if (response.ok && (text.includes('Successfully') || text.includes('updated') || text.includes('created'))) {
          // Return a success object (cast to T for type compatibility)
          return { success: true, message: text } as unknown as T;
        }

        // Otherwise, it's an error
        throw new Error(`Server returned non-JSON response: ${text.substring(0, 100)}...`);
      }

      if (!response.ok) {
        // Use centralized error handling
        let serverError;
        try {
          const errorData = await response.json();
          serverError = { status: response.status, ...errorData };
        } catch {
          try {
            const errorText = await response.text();
            serverError = { status: response.status, message: errorText };
          } catch {
            serverError = { status: response.status };
          }
        }

        const errorMessage = ErrorHandlers.silent(serverError);
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error: unknown) {

      // Retry logic for network/timeout errors
      const isRetryable = error instanceof Error && (
        error.name === 'TimeoutError' ||
        error.name === 'AbortError' ||
        error.message?.includes('fetch') ||
        error.message?.includes('network')
      );

      if (isRetryable && retryCount < API_CONFIG.RETRY_ATTEMPTS) {
        const delay = API_CONFIG.EXPONENTIAL_BACKOFF
          ? API_CONFIG.RETRY_DELAY * Math.pow(2, retryCount)
          : API_CONFIG.RETRY_DELAY;

        await new Promise(resolve => setTimeout(resolve, delay));
        return this.makeRequest<T>(endpoint, options, retryCount + 1);
      }

      throw error;
    }
  }

  // Get all restaurant tables with pagination
  async getAllTables(page = 0, size = 10): Promise<PaginatedResponse<RestaurantTable>> {
    const endpoint = `${API_ENDPOINTS.RESTAURANT_TABLE.GET_ALL}?page=${page}&size=${size}`;
    return this.makeRequest<PaginatedResponse<RestaurantTable>>(endpoint);
  }

  // Get restaurant table by ID
  async getTableById(id: number): Promise<RestaurantTable> {
    const endpoint = API_ENDPOINTS.RESTAURANT_TABLE.BY_ID(id);
    return this.makeRequest<RestaurantTable>(endpoint);
  }
  // Get restaurant table by ID (legacy method - should return single table)
  async getTableByFloorId(floorId: number): Promise<RestaurantTable> {
    const endpoint = API_ENDPOINTS.RESTAURANT_TABLE.BY_FLOORID(floorId);
    return this.makeRequest<RestaurantTable>(endpoint);
  }

  // Get all restaurant tables by floor ID 
  async getTablesByFloorId(floorId: number): Promise<RestaurantTable[]> {
    const endpoint = API_ENDPOINTS.RESTAURANT_TABLE.BY_FLOORID(floorId);
    const response = await this.makeRequest<PaginatedResponse<RestaurantTable> | RestaurantTable[]>(endpoint);
    // Handle paginated response
    return 'content' in response ? response.content : response;
  }

  // Create new restaurant table
  async createTable(tableRequest: RestaurantTableRequest): Promise<RestaurantTable> {

    try {
      // Backend returns plain text, not JSON
      const result = await this.makeRequest<{ success: boolean; message: string }>(API_ENDPOINTS.RESTAURANT_TABLE.BASE, {
        method: 'POST',
        body: JSON.stringify(tableRequest),
      });


      // After successful create, fetch the newly created table by name and floorId
      // Since backend doesn't return the created object, we need to reload
      const allTables = await this.getAllTablesFlat();
      const createdTable = allTables.find(t =>
        t.name === tableRequest.name &&
        t.floorId === tableRequest.floorId
      );

      if (!createdTable) {
        throw new Error(`Table created but not found: ${tableRequest.name} on floor ${tableRequest.floorId}`);
      }

      return createdTable;
    } catch (error) {

      // If error contains "created successfully", still try to fetch the table
      if (error instanceof Error && error.message.includes('created successfully')) {
        const allTables = await this.getAllTablesFlat();
        const createdTable = allTables.find(t =>
          t.name === tableRequest.name &&
          t.floorId === tableRequest.floorId
        );
        if (createdTable) {
          return createdTable;
        }
      }
      throw error;
    }
  }

  // Update multiple restaurant tables
  async updateTables(tables: RestaurantTable[]): Promise<string> {

    try {
      const result = await this.makeRequest<string>(API_ENDPOINTS.RESTAURANT_TABLE.BASE, {
        method: 'PUT',
        body: JSON.stringify(tables),
      });
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Update single table (convenience method)
  async updateTable(table: RestaurantTable): Promise<string> {
    return this.updateTables([table]);
  }

  // Update table status - for batch status changes
  async updateTableStatus(tableIds: number[], status: 'Available' | 'Occupied' | 'Reserved' | 'Maintenance'): Promise<string> {
    const endpoint = `${API_ENDPOINTS.RESTAURANT_TABLE.UPDATE_STATUS}?ids=${tableIds.join(',')}&status=${status.toUpperCase()}`;
    return this.makeRequest<string>(endpoint, {
      method: 'POST',
    });
  }

  // Check if table has active reservations before deletion
  async checkTableReservations(id: number): Promise<{ hasReservations: boolean, count: number, details: string[] }> {
    try {
      // Import reservationApi dynamically to avoid circular dependencies
      const { reservationApi } = await import('./reservationApi');
      const { BookingStatus } = await import('../types/reservations');

      // Add timeout to prevent hanging (10 seconds max)
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Reservation check timed out')), 10000)
      );

      // Get recent reservations (use 'month' for faster query instead of 'year')
      // Backend only supports: 'today', 'week', 'month', 'year'
      const reservationPromise = reservationApi.searchReservationsByPeriodWithParams(
        new Date().toISOString().split('T')[0],
        'month'
      );

      const allReservations = await Promise.race([reservationPromise, timeoutPromise]);

      const tableReservations = allReservations.filter((r: ReserveTableDTO) =>
        r.restaurantTableId && r.restaurantTableId.some(table => table.id === id)
      );

      // Filter out only truly inactive statuses
      const activeReservations = tableReservations.filter((r: ReserveTableDTO) =>
        r.status !== BookingStatus.REJECTED &&
        r.status !== BookingStatus.COMPLETED &&
        r.status !== BookingStatus.CANCELED
      );

      return {
        hasReservations: activeReservations.length > 0,
        count: activeReservations.length,
        details: activeReservations.map((r: ReserveTableDTO) =>
          `${r.status} reservation for ${r.guestNo} guests on ${r.date} at ${r.time} (${r.username})`
        )
      };
    } catch (error) {
      // If reservation check fails (timeout or error), allow deletion but warn user
      return { hasReservations: false, count: 0, details: [] };
    }
  }

  // Delete restaurant table by ID with reservation check
  async deleteTable(id: number): Promise<string> {
    try {
      // Check for active reservations first (with timeout protection)
      const reservationCheck = await this.checkTableReservations(id);

      if (reservationCheck.hasReservations) {
        throw new Error(
          `Cannot delete this table because it has ${reservationCheck.count} existing reservation(s). ` +
          `Please cancel or move the reservations first:\n\n${reservationCheck.details.join('\n')}`
        );
      }

      const endpoint = API_ENDPOINTS.RESTAURANT_TABLE.BY_ID(id);
      return await this.makeRequest<string>(endpoint, {
        method: 'DELETE',
      });
    } catch (error) {
      // Handle backend constraint violations
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('existing reservations') ||
        errorMessage.includes('Cannot delete this table')) {
        throw error; // Re-throw user-facing errors
      }

      // Handle other errors
      throw new Error(
        errorMessage ||
        'Failed to delete table. It may have existing reservations or dependencies.'
      );
    }
  }

  // Get all tables without pagination (for dropdowns/selections)
  async getAllTablesFlat(): Promise<RestaurantTable[]> {
    // Get a large page size to fetch all tables
    const response = await this.getAllTables(0, 1000);
    return response.content;
  }

  // Get available tables for a specific date/time using the new backend endpoint
  async getAvailableTables(date?: string, time?: string): Promise<RestaurantTable[]> {
    const allTables = await this.getAllTablesFlat();

    // If no date/time specified, return all available tables
    if (!date || !time) {
      return allTables.filter(table => table.status === 'Available');
    }

    // Filter out tables that are already booked for the specified time
    // This would require checking against reservations, but for now return available tables
    return allTables.filter(table => table.status === 'Available');
  }

  // Get available tables for a specific date, time, and floor from the backend
  async getAvailableTablesByDateTime(date: string, time: string, floorId: number): Promise<RestaurantTable[]> {
    const endpoint = `${API_ENDPOINTS.RESTAURANT_TABLE.GET_AVAILABLE_BY_DATE_TIME}?date=${date}&time=${time}&floorId=${floorId}`;

    try {
      const response = await this.makeRequest<RestaurantTable[] | PaginatedResponse<RestaurantTable>>(endpoint);

      // Handle both array and paginated response formats
      const tables = Array.isArray(response) ? response : response.content;
      return tables;
    } catch (error) {
      // Fallback to fetching all tables by floor and filtering by status
      const floorTables = await this.getTablesByFloorId(floorId);
      return floorTables.filter(table => table.status === 'Available');
    }
  }


  // Get tables by floor (legacy method - use floorId for new code)
  async getTablesByFloor(floor: string): Promise<RestaurantTable[]> {
    const allTables = await this.getAllTablesFlat();
    // Support both legacy floor string and new floorId number
    return allTables.filter(table =>
      table.floor === floor ||
      table.floorId === parseInt(floor) ||
      (floor === '1' && table.floorId === 1) ||
      (floor === '2' && table.floorId === 2) ||
      (floor === '3' && table.floorId === 3)
    );
  }

  // Get tables by status
  async getTablesByStatus(status: string): Promise<RestaurantTable[]> {
    const allTables = await this.getAllTablesFlat();
    return allTables.filter(table => table.status === status);
  }

  // Get table seats summary
  async getSeatsSummary(): Promise<{
    totalTables: number;
    totalSeats: number;
    availableSeats: number;
    occupiedTables: number;
    availableTables: number;
  }> {
    const allTables = await this.getAllTablesFlat();

    const totalTables = allTables.length;
    const totalSeats = allTables.reduce((sum, table) => sum + table.seats, 0);
    const availableTables = allTables.filter(table => table.status === 'Available');
    const occupiedTables = allTables.filter(table => table.status === 'Occupied' || table.status === 'Reserved');
    const availableSeats = availableTables.reduce((sum, table) => sum + table.seats, 0);

    return {
      totalTables,
      totalSeats,
      availableSeats,
      occupiedTables: occupiedTables.length,
      availableTables: availableTables.length,
    };
  }
}

// Export singleton instance
export const tableApi = new RestaurantTableApiService();
export default tableApi;